import {
  BasicOverlay,
  BasicOverlayData,
} from "$/game/prefabs/scenes/overlays/BasicOverlay.ts";
import { RoundedTextButton } from "$/game/prefabs/ui/RoundedTextButton.ts";
import { Pet } from "$/domain/pet/types.ts";

export interface FoodShopOverlayData extends BasicOverlayData {
  pet: Pet;
}

export class FoodShopOverlay extends BasicOverlay {
  private quantity: number = 1;
  private pricePerUnit: number = 5;
  private quantityText!: Phaser.GameObjects.Text;
  private priceText!: Phaser.GameObjects.Text;
  private petData!: Pet;

  constructor() {
    super({ key: "FoodShopOverlay" });
  }

  override create(data: FoodShopOverlayData) {
    super.create({ ...data, showCloseButton: true });

    this.petData = data.pet;
    const { width } = this.cameras.main;
    const selectorY = 480;

    // Title text (without icon, like in other overlays)
    this.add.text(
      width / 2,
      350,
      this.keys.strings.foodShopOverlay.title,
      {
        fontSize: "48px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
      },
    ).setOrigin(0.5, 0.5);

    // Food icon next to quantity selector
    this.add.image(
      width / 2 - 140,
      selectorY,
      this.atlas,
      this.keys.assets.sprites.food.carrot,
    );

    // Minus button
    const minusButton = new RoundedTextButton(
      this,
      width / 2 - 50,
      selectorY,
      "−",
      60,
      60,
      15,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "bold",
        fontFamily: "Phantom-Muff",
        color: "#735933",
      },
    );
    this.add.existing(minusButton);
    minusButton.on("pointerdown", () => this.decreaseQuantity());

    // Quantity text
    this.quantityText = this.add.text(
      width / 2 + 20,
      selectorY,
      `${this.quantity}`,
      {
        fontSize: "48px",
        color: "#735933",
        fontFamily: "Phantom-Muff",
        fontStyle: "bold",
      },
    );
    this.quantityText.setOrigin(0.5, 0.5);

    // Plus button
    const plusButton = new RoundedTextButton(
      this,
      width / 2 + 90,
      selectorY,
      "+",
      60,
      60,
      15,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "bold",
        fontFamily: "Phantom-Muff",
        color: "#735933",
      },
    );
    this.add.existing(plusButton);
    plusButton.on("pointerdown", () => this.increaseQuantity());

    // Buy button with price
    const buyButton = new RoundedTextButton(
      this,
      width / 2,
      selectorY + 100,
      "",
      280,
      72,
      20,
      this.global.TUNER_TWA_CONFIG.palette.primary,
      this.global.TUNER_TWA_CONFIG.palette.primary + 0x100000,
      {
        fontSize: "40px",
        fontStyle: "normal",
        fontFamily: "Phantom-Muff",
        color: "#735933",
      },
    );
    this.add.existing(buyButton);

    // Price text inside button
    this.priceText = this.add.text(
      -20,
      0,
      `${this.getTotalPrice()}`,
      {
        fontSize: "40px",
        fontFamily: "Phantom-Muff",
        fontStyle: "bold",
        color: "#735933",
      },
    );
    this.priceText.setOrigin(0.5, 0.5);
    buyButton.add(this.priceText);

    // Egg icon in button
    const eggIcon = this.add.image(
      this.priceText.width / 2 + 20,
      0,
      this.atlas,
      this.keys.assets.sprites.ui.crystalUi,
    );
    eggIcon.setScale(0.9);
    buyButton.add(eggIcon);

    // Buy button handler
    buyButton.on("pointerdown", () => this.handleBuy());
  }

  private getTotalPrice(): number {
    return this.quantity * this.pricePerUnit;
  }

  private updateDisplay(): void {
    this.quantityText.setText(`${this.quantity}`);
    this.priceText.setText(`${this.getTotalPrice()}`);
  }

  private decreaseQuantity(): void {
    if (this.quantity > 1) {
      this.quantity--;
      this.updateDisplay();
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.selectionChanged();
      }
    }
  }

  private increaseQuantity(): void {
    if (this.quantity < 99) {
      this.quantity++;
      this.updateDisplay();
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.selectionChanged();
      }
    }
  }

  private async handleBuy(): Promise<void> {
    const totalPrice = this.getTotalPrice();
    const sceneData = this.scene.settings.data as FoodShopOverlayData;

    if (this.petData.coins < totalPrice) {
      // Not enough coins
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.notificationOccurred("error");
      }
      this.close();
      if (sceneData?.fromScene) {
        const parentScene = this.scene.get(sceneData.fromScene);
        if (parentScene && "transition" in parentScene) {
          (parentScene as {
            transition: { toScene: (scene: string, text: string) => void };
          }).transition.toScene(
            "CurrencyShopScene",
            this.keys.strings.foodShopOverlay.notEnoughCoins,
          );
        }
      }
      return;
    }

    try {
      // Buy food multiple times
      for (let i = 0; i < this.quantity; i++) {
        await this.api.pet.buyFood();
      }

      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.notificationOccurred("success");
      }

      this.showConfetti();
      this.close();
    } catch (error) {
      console.error("Error buying food:", error);
      if (this.settings.getSetting("isVibrationEnabled")) {
        this.telegram.twaSDK.hapticFeedback.notificationOccurred("error");
      }
    }
  }

  private showConfetti(): void {
    try {
      const jsConfetti = new (window as unknown as {
        JSConfetti: new () => { addConfetti: () => void };
      }).JSConfetti();
      jsConfetti.addConfetti();
    } catch (error) {
      console.error("Error showing confetti:", error);
    }
  }
}
